/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  TreeSelect,
  Modal,
  Spin,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
} from '@ant-design/icons';
import NumericInput from 'components/NumericInput';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Table from 'components/Table';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import {
  deleteClase,
  getClases,
  patchClase,
  postClase,
} from 'api/catalogos/clases';
import { getTipos } from 'api/catalogos';
import API from 'utils/api';
import { ActionsNames, hasActionPermission } from 'utils/permission/index';
import { isMutable } from 'utils/estadosGlobales';

const baseURI = 'catalogos/clases/';
const catalogsModels = ['rubro', 'tipo'];

const Clases = ({ permission }) => {
  const user = useSelector(({ auth }) => auth.user);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ results: [], count: 0 });
  const [dataTree, setDataTree] = useState([]);
  const [rubros, setRubros] = useState([]);
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const entidad = useSelector(({ auth }) => auth.entidad);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [mutable, setMutable] = useState(true);

  const onClickTitle = (e) => {
    const element = e.target;
    if (element) {
      let parent = element.parentElement;
      parent = parent ? parent.parentElement : null;
      if (parent) {
        const clickableSpan = parent.previousSibling;
        if (clickableSpan) {
          clickableSpan.click();
        }
      }
    }
  };

  const renderTitle = (title, bold) => (
    // eslint-disable-next-line
    <span onClick={onClickTitle} className={bold ? 'bold' : ''}>
      {title}
    </span>
  );

  const normalizeData = (_data) => {
    if (_data) {
      const output = _data.map((item) => {
        const clone = { ...item };
        const children = normalizeData(clone.children);
        if (!children.length) {
          delete clone.children;
          return ({
            title: renderTitle(`${clone.clave} - ${clone.denominacion}`),
            value: clone.id,
            key: clone.id,
          });
        }
        return ({
          title: renderTitle(`${clone.clave} - ${clone.denominacion}`),
          value: clone.denominacion,
          children,
          selectable: false,
          // disabled: true,
        });
      });
      return output;
    }
    return false;
  };

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const res = await getClases({ page, ...q });
    setData(res);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    let hasAllPermission = true;
    const validatePermission = () => {
      const _hasAllViewPermission = catalogsModels
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      hasAllPermission = _hasAllViewPermission;
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const resTipos = await getTipos();
      const _data = await getClases({ page: 1 });
      const normalized = normalizeData(resTipos);
      if (mounted) {
        setData(_data);
        setRubros(resTipos);
        setDataTree(normalized);
        setLoading(false);
      }
    };
    validatePermission();
    if (hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    form.resetFields();
  };

  const onFinish = async () => {
    setLoading(true);
    await form.validateFields();
    const values = form.getFieldsValue();
    values.pais = entidad.pais;
    const [key] = selectedRowKeys;
    const saved = await (key ? patchClase(key, values) : postClase(values));
    if (saved) {
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteClase(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    setMutable(isMutable(record));
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = () => {
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 150,
    },
    {
      titleText: 'Denominación',
      dataIndex: 'denominacion',
      key: 'denominacion',
      width: 550,
    },
    {
      titleText: 'Nivel',
      dataIndex: 'nivel',
      key: 'nivel',
      width: 150,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      // render: (val) => (estadosGlobales.find((item) => item.id === val)?.descripcion),
      catalog: {
        cat: estadosGlobales,
        labelProp: 'descripcion',
        use: true,
      },
    },
  ];

  const rules = {
    required: [{ required: true }],
  };

  const onSelectTipo = async (val) => {
    try {
      setLoading(true);
      const rubro = rubros.find((r) => r.children.find((t) => t.id === val));
      const tipo = rubro.children.find((t) => t.id === val);
      const last = [...data.results].filter((e) => e.tipo === tipo.id).pop()
        ?.clave.replace(tipo.clave, '') || 0;
      form.setFieldsValue({
        clave: `${tipo.clave}${parseInt(last, 10) + 1}`,
      });
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const authorizeAll = async () => {
    setLoading(true);
    const promises = data.results.map((e) => API.patch(`${baseURI}${e.id}/`, { estados_globales: 4 }));
    await Promise.all(promises);
    await fetchData(currentPage);
    setLoading(false);
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin
        tip="Cargando..."
        spinning={loading}
      >
        <Table
          cols={columns}
          data={data?.results || []}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete,
          }}
          customActions={[
            user?.email.includes('sigob') ? {
              icon: <CheckCircleOutlined />,
              text: 'Autorizar Todos',
              onClick: authorizeAll,
              disabledBySelection: false,
              hasPermission: permission?.change,
            } : {},
          ]}
          rowKey="id"
          allowImport
          baseURI={baseURI}
          permission={permission}
          hasAllViewPermission={hasAllViewPermission}
          pagination={false}
          count={data.count}
          paginationCallback={fetchData}
          currentPage={currentPage}
        />
        <Modal
          visible={visible}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Clase`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          footer={null}
          closable={false}
          keyboard={!loading}
          maskClosable={!loading}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            initialValues={{ estados_globales: 1 }}
          >
            <Row>
              <Col span={24}>
                <Form.Item
                  name="tipo"
                  label="Tipo"
                  rules={rules.required}
                >
                  <TreeSelect
                    onSelect={onSelectTipo}
                    showSearch
                    treeNodeFilterProp="title"
                    style={{ width: '100%' }}
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    treeData={dataTree}
                    allowClear
                    disabled={!mutable}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <NumericInput
                  name="clave"
                  label="Clave"
                  disabled={!!selectedRowKeys.length || !mutable}
                  required
                />
              </Col>
              <Col span={24}>
                <Form.Item
                  name="denominacion"
                  label="Denominación"
                  rules={rules.required}
                  hasFeedback
                >
                  <Input allowClear disabled={!mutable} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  rules={rules.required}
                  name="estados_globales"
                  label="Estado"
                  hasFeedback
                >
                  <Select
                    disabled={!selectedRowKeys.length || !mutable}
                    dataSource={estadosGlobales}
                  />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            onCancel();
          }}
          visible={visibleAlert}
          content={`Clase ${form.getFieldValue('denominacion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default Clases;
